/**
 * ShooglingPage
 *
 * Operational dashboard showing production batches that need shoogling (mixing).
 * Shoogling is the process of mixing/tumbling spawn with substrate after inoculation.
 *
 * Displays batches in INOCULATED state, allowing operators to mark them as shoogled.
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useShooglingBatches, useShooglingMutations } from '@/hooks/useShooglingBatches'
import { Shuffle, RefreshCw, CheckCircle, AlertTriangle, Undo2 } from 'lucide-react'

export default function ShooglingPage({ user, onLogout }) {
  const { batches, loading, error, refetch } = useShooglingBatches()
  const { markAsShoogled, undoShoogled, loading: mutationLoading } = useShooglingMutations()
  const [processingIds, setProcessingIds] = useState(new Set())
  const [batchToConfirm, setBatchToConfirm] = useState(null)
  const [recentlyShoogled, setRecentlyShoogled] = useState([])

  const handleUndo = async (batch) => {
    setProcessingIds(prev => new Set(prev).add(batch.id))
    try {
      await undoShoogled(batch.id)
      setRecentlyShoogled(prev => prev.filter(b => b.id !== batch.id))
      refetch()
      toast.success(`${batch.batch_code} reverted`)
    } catch (err) {
      toast.error(`Failed to undo: ${err.message}`)
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(batch.id)
        return next
      })
    }
  }

  const handleConfirmShoogled = async () => {
    if (!batchToConfirm) return
    const batch = batchToConfirm
    setBatchToConfirm(null)

    setProcessingIds(prev => new Set(prev).add(batch.id))
    try {
      await markAsShoogled(batch.id)
      setRecentlyShoogled(prev => [...prev, batch])
      refetch()
    } catch (err) {
      console.error('Failed to mark as shoogled:', err)
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(batch.id)
        return next
      })
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <PageLayout
        title="Shoogling"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-content-secondary">Loading...</div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Shoogling"
      user={user}
      onLogout={onLogout}
      headerActions={
        <Button
          variant="outline"
          size="sm"
          onClick={refetch}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      }
    >
      <div className="space-y-6">
        {/* Summary Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shuffle className="h-5 w-5" />
              Bags Awaiting Shoogling
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold text-foreground">
              {batches.length}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Inoculated batches ready to be mixed
            </p>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Batches Table */}
        <Card>
          <CardContent className="p-0">
            {batches.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="text-lg font-medium">All caught up!</p>
                <p className="text-sm mt-1">No batches need shoogling at the moment.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Done</TableHead>
                    <TableHead>Batch Code</TableHead>
                    <TableHead>Strain</TableHead>
                    <TableHead>Inoculation Date</TableHead>
                    <TableHead className="text-right">Blocks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => {
                    const isProcessing = processingIds.has(batch.id)
                    return (
                      <TableRow key={batch.id}>
                        <TableCell>
                          <Checkbox
                            checked={false}
                            disabled={isProcessing || mutationLoading}
                            onCheckedChange={() => setBatchToConfirm(batch)}
                            className="data-[state=checked]:bg-green-600"
                          />
                        </TableCell>
                        <TableCell className="font-medium">{batch.batch_code}</TableCell>
                        <TableCell>
                          <span className="font-medium">{batch.strain_code}</span>
                          <span className="text-muted-foreground ml-1">{batch.strain_name}</span>
                        </TableCell>
                        <TableCell>{formatDate(batch.inoculation_date)}</TableCell>
                        <TableCell className="text-right">{batch.total_blocks}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        {/* Recently Shoogled — with undo */}
        {recentlyShoogled.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Recently Shoogled</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {recentlyShoogled.map((batch) => {
                const isProcessing = processingIds.has(batch.id)
                return (
                  <div
                    key={batch.id}
                    className="flex items-center justify-between rounded-md border px-4 py-2"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-mono font-medium">{batch.batch_code}</span>
                      <span className="text-muted-foreground text-sm">
                        {batch.strain_code} — {batch.total_blocks} blocks
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isProcessing}
                      onClick={() => handleUndo(batch)}
                    >
                      <Undo2 className="h-4 w-4 mr-1" />
                      Undo
                    </Button>
                  </div>
                )
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation dialog */}
      <AlertDialog
        open={!!batchToConfirm}
        onOpenChange={(open) => !open && setBatchToConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Shoogled?</AlertDialogTitle>
            <AlertDialogDescription>
              This will mark batch{' '}
              <span className="font-mono font-medium">
                {batchToConfirm?.batch_code}
              </span>{' '}
              ({batchToConfirm?.strain_code} — {batchToConfirm?.total_blocks} blocks) as shoogled
              and move it to incubation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmShoogled}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  )
}
